/**
 * English display copy — a translation of copy-pl.ts (the PL mock-up is the
 * source of truth). Structure and key set must stay identical to the PL file.
 */
import type { SiteCopy } from "./copy.js";

export const enCopy: SiteCopy = {
	brand: {
		name: "MB Group",
		tagline: "The Circular ECOsystem",
		quote: "“We are building the future of the closed loop”",
		blurb:
			"The Circular ECOsystem — recycling, re-use, collection, the deposit-return scheme and ESG in one service ecosystem.",
		mockupNote: "Concept mock-up · July 2026",
	},
	nav: {
		services: "Services",
		industries: "Industries",
		cases: "Case studies",
		knowledge: "Insights",
		about: "The Group",
		contact: "Contact",
		consult: "Book a consultation",
		search: "Search",
		menu: "Menu",
		close: "Close",
		megaEyebrow: "Services by category — not by legal entity",
		fullPortfolio: "Full service portfolio →",
		start: "Home",
	},
	home: {
		heroEyebrow: "MB Group · Circular ECOsystem",
		heroTitle: "One group. A complete circular service ecosystem.",
		heroLead:
			"Recycling, re-use, textiles, collection logistics, the deposit-return scheme and ESG — organised into service categories, not into a corporate structure. You choose the outcome, we assign the resources.",
		heroSearchPlaceholder: "Type the service you need, or describe the problem…",
		heroCtaPrimary: "Find a solution",
		heroCtaSecondary: "Browse services",
		heroStats: [
			{ value: "9", label: "companies in one ecosystem" },
			{ value: "8", label: "service categories to choose from" },
			{ value: "1", label: "contract and a single account manager" },
		],
		heroFigureCaption: "The closed loop at MB Group — from collection to raw material and report.",
		heroFigureAlt:
			"Circular-economy diagram: collection, sorting, processing, secondary raw material, re-use and reporting joined into a closed loop.",
		ticker: [
			"Li-ion batteries",
			"WEEE",
			"Refrigeration",
			"PCB & electronics",
			"Textiles",
			"RE-USE",
			"Deposit-return scheme",
			"EPR",
			"ADR transport",
			"Waste records",
			"CSRD report",
			"Carbon footprint",
		],
		videoEyebrow: "MB Group Think Tank",
		videoTitle: "Regulation moves faster than your budget.",
		videoLead:
			"The deposit-return scheme, EPR for textiles, the EU battery regulation, CSRD. Our think tank turns new rules into concrete operational decisions — before they turn into cost.",
		videoStats: [
			{ value: "4 areas", label: "deposit · EPR · batteries · ESG" },
			{ value: "No jargon", label: "conclusions, not statute quotes" },
		],
		videoPlay: "Play video",
		videoPause: "Pause",
		videoMuteOn: "Unmute",
		videoMuteOff: "Mute",
		videoMore: "More analyses →",
		navigatorEyebrow: "Services, not companies",
		navigatorTitle: "You navigate by need, not by our org chart.",
		navigatorLead:
			"Behind every service sits a specialised company and real infrastructure. You pick the business outcome — we assign the resources of the whole MB Group ecosystem.",
		navigatorBadge: "Category",
		navigatorConsult: "Book a consultation →",
		navigatorStatLabel: "services in this category — one contract, one account manager",
		proofEyebrow: "Real infrastructure",
		proofTitle: "Behind every service stands a plant, a fleet and a team — not a slide deck.",
		proofLead:
			"Our own processing plants, a textile sorting facility, ADR transport, a collection-point network and compliance teams. We serve manufacturers, retail, municipalities and institutions across Poland and on export markets.",
		proofFacts: [
			{ value: "7 plants", label: "processing and sorting" },
			{ value: "ADR", label: "own fleet for Li-ion batteries" },
			{ value: "Nationwide", label: "collection and return-point network" },
			{ value: "CSRD", label: "reporting under ESRS" },
		],
		industriesTitle: "Solutions for your industry",
		industriesAll: "All industries →",
		magnetsEyebrow: "Expert materials",
		magnetsTitle: "Knowledge you can put to work immediately.",
		magnetsLead:
			"Reports, webinars and case studies prepared by the operating teams — not by a marketing department.",
		magnetsAll: "Full knowledge base →",
		ctaEyebrow: "Quick contact",
		ctaTitle: "Describe the problem — we will assemble services from the whole ecosystem.",
		ctaLead:
			"One contact, one offer, a complete documentation pack. You do not need to know which company in the group owns your topic.",
		ctaFacts: [
			{ value: "24 h", label: "response time on business days" },
			{ value: "1 manager", label: "for every service in the group" },
		],
	},
	newsletter: {
		title: "MB Group regulatory newsletter",
		desc: "Once a month: what changed in the rules and what it means for your company. No sales pitches.",
		emailPlaceholder: "Work e-mail",
		submit: "Subscribe",
		doneTitle: "Subscribed",
		doneDesc:
			"We have sent a confirmation e-mail to the address you provided. You can unsubscribe with one click in every message.",
		kind: "Newsletter",
		tag: "Regulatory newsletter",
	},
	quickForm: {
		title: "We will call you back",
		desc: "Leave a number and pick a convenient slot — no half-page forms.",
		name: "Full name",
		phone: "Phone",
		slotLabel: "Preferred time",
		slots: ["Morning 8–11", "Midday 11–14", "Afternoon 14–17", "Any time"],
		submit: "Request a call",
		mailInstead: "Prefer to write? Go to the contact form →",
		sentTitle: "Request received",
		sentDesc: "The category manager will call you back in the chosen slot.",
		sendAnother: "Send another",
	},
	services: {
		title: "The full service portfolio — organised into eight categories.",
		lead: "No company names, no internal acronyms. You pick the service; we assign the right plant, fleet and team from the group.",
		viewService: "View service →",
		inCategory: "services in this category",
	},
	serviceDetail: {
		ask: "Ask about this service →",
		allServices: "All services",
		scopeEyebrow: "Scope and specialisations",
		scopeTitle: "What exactly we deliver",
		groupsEyebrow: "Product groups",
		groupsTitle: "What we accept and handle",
		notListedTitle: "Cannot see your waste stream?",
		notListedDesc:
			"We also handle mixed and non-standard streams — describe your case and we will put together a service set.",
		notListedCta: "Describe your case →",
		processEyebrow: "How we work together",
		processTitle: "Four steps from enquiry to documentation",
		process: [
			{
				n: "01",
				title: "Enquiry and analysis",
				desc: "You describe the stream or the goal — we come back with a service set and follow-up questions.",
			},
			{
				n: "02",
				title: "Offer and contract",
				desc: "Pricing, logistics terms and the documentation scope in a single document.",
			},
			{
				n: "03",
				title: "Delivery",
				desc: "Collection, processing or implementation on the agreed schedule.",
			},
			{
				n: "04",
				title: "Documentation and report",
				desc: "A complete document pack plus tonnage and outcome figures for your own reporting.",
			},
		],
		relatedTitle: "Other services in category",
		relatedAll: "Full portfolio →",
	},
	about: {
		eyebrow: "MB Group · The Circular ECOsystem",
		title: "A group that closes the loop — from collection to raw material and report.",
		lead: "Trade-fair footage — the teams, plants and conversations behind every service in the portfolio.",
		bodyTitle: "One group, nine specialised organisations.",
		bodyParagraphs: [
			"WEEE, battery and electronics processing plants, a textile sorting facility, deposit-scheme operations, collection networks, RE-USE trading and ESG advisory. Clients do not need to know our structure — they only need to state the need.",
			"We operate in Poland and on export markets, serving manufacturers, retail chains, municipalities, institutions and households.",
		],
		pillarsTitle: "Group pillars",
	},
	industries: {
		title: "Solutions matched to the specifics of your industry.",
		lead: "The same service ecosystem, different priorities: compliance, cost, fire risk, reputation or value recovery.",
	},
	cases: {
		title: "Implementations you can measure.",
		lead: "Concrete projects with a number attached — audit, rollout and the outcome after year one.",
	},
	knowledge: {
		title: "Regulation, deadlines and practice — without the legal jargon.",
		lead: "Analyses written by the group's operating and advisory teams.",
		read: "Read the analysis →",
	},
	article: {
		ctaTitle: "Want to discuss this on your own case?",
		ctaDesc: "Book a call with the category manager or download the full report on the topic.",
		ctaConsult: "Book a consultation",
		ctaBack: "Back to the knowledge base",
	},
	contact: {
		title: "Describe the problem or the business goal.",
		lead: "You do not need to know which company in the group owns your topic. A description of the situation is enough — we will come back with a service set and pricing within 24 business hours.",
		points: [
			"A reply within 24 business hours",
			"One account manager for every service in the group",
			"A complete recovery and reporting documentation pack",
			"Support for multi-site clients across Poland",
		],
		formTitle: "Contact form",
		name: "Full name",
		company: "Company / institution",
		email: "Work e-mail",
		categoryPlaceholder: "Choose a service category…",
		message: "Describe the problem, the waste stream or the business goal…",
		submit: "Send enquiry",
		privacyNote:
			"By submitting the form you accept the privacy policy. Demo mock-up — data is not stored.",
		sentTitle: "Thank you for your enquiry",
		sentDesc: "Your enquiry reached the manager of the relevant service category.",
		sendAnother: "Send another",
	},
	assistant: {
		title: "MB Group assistant",
		subtitle: "Describe the problem — we will match services from the whole ecosystem",
		placeholder: "e.g. I have 200 kg of e-scooter batteries — what now?",
		frequent: "Frequent questions:",
		prompts: [
			"200 kg of e-scooter batteries",
			"EPR duties for textiles",
			"consumer returns of appliances",
			"CSRD report for a company",
			"fridges from an equipment swap",
		],
		recommendation: "Assistant recommendation",
		answerFound:
			"Based on your description we suggest starting with: “{service}”. The enquiry was tagged with the “{category}” category — leave your details and that category's manager will get back to you within 24 business hours.",
		answerEmpty:
			"We have no off-the-shelf service for this enquiry, but we do handle mixed and non-standard streams. Leave a description of your case and we will come back with a recommendation within 24 business hours.",
		privacyNote:
			"Enquiries are collected anonymously as a knowledge base of client needs and tagged to the relevant service category.",
		expertCta: "Talk to an expert →",
		openLabel: "AI assistant",
		resultsLabel: "Matched services",
	},
	gate: {
		email: "Work e-mail",
		company: "Company",
		privacyNote: "By subscribing you accept the privacy policy. Demo mock-up — data is not stored.",
		close: "Close",
	},
	resources: {
		title: "Reports and materials",
		webinarsTitle: "Webinars",
		upcoming: "Upcoming",
		recording: "Recording",
		noWebinars: "No webinars scheduled right now.",
	},
	search: {
		emptyHint: "Enter a phrase to search the site.",
		resultsCount: "{total} result(s) for “{query}”",
	},
	preview: {
		title: "Preview unavailable",
		desc: "Draft preview is limited to signed-in editors.",
		signIn: "Sign in",
		forbidden: "Your account is not allowed to preview drafts.",
	},
	panel: {
		title: "Editor panel",
		signInLead:
			"Sign in with your CMS (Strapi) account. The same credentials open the content panel and draft previews on the site.",
		email: "E-mail",
		password: "Password",
		submit: "Sign in",
		submitting: "Signing in…",
		errorInvalid: "Incorrect e-mail or password.",
		errorRateLimited: "Too many sign-in attempts. Try again in a few minutes.",
		errorUnavailable: "Sign-in is temporarily unavailable. Please try again shortly.",
		signedInAs: "Signed in as",
		rolesTitle: "Your roles",
		noEditorRole:
			"This role does not include draft preview on the site (editors, approvers and administrators have it). Edit content in the CMS.",
		cmsTitle: "Content in the CMS",
		cmsDesc:
			"Edit services, articles, case studies and site settings. Sign in with the same credentials.",
		cmsCta: "Open the CMS",
		previewTitle: "Draft preview",
		previewDesc:
			"Changes saved in the CMS but not yet published are shown at /preview/<type>/<slug>, e.g. /preview/service/<service-slug>. Types: service, company, case-study, article.",
		signOut: "Sign out",
		roleNames: {
			group_admin: "Group administrator",
			domain_admin: "Site administrator",
			domain_editor: "Editor",
			content_approver: "Content approver",
			translator: "Translator",
			marketing: "Marketing",
			sales: "Sales",
			analyst: "Analyst",
		},
	},
	footer: {
		servicesTitle: "Services",
		groupTitle: "Group",
		contactTitle: "Contact",
		locations: "Micigózd · Głogów Małopolski\nStąporków · Sztum · Kielce\nWarsaw",
		rights: "All rights reserved.",
	},
	status: {
		notFoundTitle: "We could not find that page",
		notFoundDesc:
			"The address may have changed or the material was moved. Have a look at the full service portfolio.",
		notFoundCta: "Go to services",
		errorTitle: "Something went wrong",
		errorDesc: "This section could not be loaded. Please try again.",
		retry: "Try again",
	},
	demo: {
		bannerTitle: "Demonstration mode",
		bannerBody:
			"The content management system is temporarily unreachable — you are seeing a stored copy of the service catalogue. Form submissions are not saved in this mode.",
	},
	a11y: {
		mainNav: "Main navigation",
		breadcrumb: "Breadcrumb",
		servicesMenu: "Service categories",
	},
};
